/**
 * Analytics Context — Google Analytics only (App Insights removed).
 */

import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAppSelector } from '../store/hooks';
import { analyticsService } from '../services/analytics.service';
import { googleAnalyticsService } from '../services/google-analytics.service';

interface AnalyticsContextValue {
  analyticsService: typeof analyticsService;
  googleAnalyticsService: typeof googleAnalyticsService;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const { userInfo, isAuthenticated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    const gaMeasurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    if (gaMeasurementId) {
      googleAnalyticsService.initialize(gaMeasurementId);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && userInfo) {
      googleAnalyticsService.setUserId(userInfo.userId);
      googleAnalyticsService.setUserProperties({ user_type: 'authenticated' });
    }
  }, [isAuthenticated, userInfo]);

  const value: AnalyticsContextValue = {
    analyticsService,
    googleAnalyticsService,
  };

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

export const useAnalytics = (): AnalyticsContextValue => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
